import { z } from "zod";
import { CURRENT_SCHEMA_VERSION } from "../types/template.js";

const color = z.string().min(1);
const spacing = z.object({ top: z.number().optional(), right: z.number().optional(), bottom: z.number().optional(), left: z.number().optional() }).partial();

const borderSpec = z
  .object({
    width: z.number().min(0).optional(),
    style: z.enum(["solid", "dashed", "dotted", "none"]).optional(),
    color: color.optional(),
    radius: z.number().min(0).optional(),
    sides: z.array(z.enum(["top", "right", "bottom", "left"])).optional()
  })
  .partial();

export const blockStyleSchema = z
  .object({
    textStyle: z.string().optional(),
    fontFamily: z.string().optional(),
    fontSize: z.number().positive().optional(),
    fontWeight: z.union([z.number(), z.enum(["normal", "bold"])]).optional(),
    fontStyle: z.enum(["normal", "italic"]).optional(),
    lineHeight: z.number().positive().optional(),
    letterSpacing: z.number().optional(),
    textTransform: z.enum(["none", "uppercase", "lowercase", "capitalize"]).optional(),
    textAlign: z.enum(["left", "center", "right", "justify"]).optional(),
    verticalAlign: z.enum(["top", "middle", "bottom"]).optional(),
    color: color.optional(),
    background: color.optional(),
    padding: spacing.optional(),
    margin: spacing.optional(),
    border: borderSpec.optional(),
    width: z.union([z.number(), z.string()]).optional(),
    height: z.number().optional(),
    align: z.enum(["left", "center", "right"]).optional(),
    opacity: z.number().min(0).max(1).optional()
  })
  .partial();

const conditionRule = z.object({
  left: z.string(),
  operator: z.enum(["eq", "neq", "gt", "gte", "lt", "lte", "contains", "startsWith", "endsWith", "in", "isEmpty", "isNotEmpty", "isTrue", "isFalse"]),
  right: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional()
});

export const conditionGroupSchema: z.ZodType<import("../types/template.js").ConditionGroup> = z.lazy(() =>
  z.object({ op: z.enum(["and", "or"]), rules: z.array(z.union([conditionRule, conditionGroupSchema])) })
);

const conditionSchema = z.union([z.string(), conditionGroupSchema]);

const flowSchema = z
  .object({
    breakBefore: z.enum(["auto", "page"]).optional(),
    breakAfter: z.enum(["auto", "page"]).optional(),
    keepTogether: z.boolean().optional(),
    keepWithNext: z.boolean().optional(),
    minOrphans: z.number().int().min(0).optional(),
    minWidows: z.number().int().min(0).optional()
  })
  .partial();

export const nodeSchema: z.ZodType<import("../types/template.js").Node> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    props: z.record(z.unknown()),
    style: blockStyleSchema.optional(),
    children: z.array(nodeSchema).optional(),
    visibleWhen: conditionSchema.optional(),
    flow: flowSchema.optional(),
    name: z.string().optional(),
    locked: z.boolean().optional()
  })
);

const textStyleSchema = z
  .object({
    fontFamily: z.string().optional(),
    fontSize: z.number().positive().optional(),
    fontWeight: z.union([z.number(), z.enum(["normal", "bold"])]).optional(),
    fontStyle: z.enum(["normal", "italic"]).optional(),
    lineHeight: z.number().positive().optional(),
    letterSpacing: z.number().optional(),
    textTransform: z.enum(["none", "uppercase", "lowercase", "capitalize"]).optional(),
    color: color.optional(),
    marginTop: z.number().optional(),
    marginBottom: z.number().optional(),
    keepWithNext: z.boolean().optional()
  })
  .partial();

const pageRegion = z.object({ height: z.number().min(0), differentFirstPage: z.boolean() });

export const pageSettingsSchema = z.object({
  size: z.union([z.enum(["A4", "A3", "A5", "Letter", "Legal", "Tabloid"]), z.object({ width: z.number().positive(), height: z.number().positive() })]),
  orientation: z.enum(["portrait", "landscape"]),
  margins: z.object({ top: z.number().min(0), right: z.number().min(0), bottom: z.number().min(0), left: z.number().min(0) }),
  header: pageRegion,
  footer: pageRegion,
  background: color.optional(),
  minPages: z.number().int().min(1).optional()
});

export const documentStylesSchema = z.object({
  text: z.record(textStyleSchema),
  table: z
    .object({
      fontSize: z.number().optional(),
      headerBackground: color.optional(),
      headerColor: color.optional(),
      headerFontWeight: z.union([z.number(), z.enum(["normal", "bold"])]).optional(),
      borderColor: color.optional(),
      borderWidth: z.number().optional(),
      borders: z.enum(["all", "horizontal", "outer", "none"]).optional(),
      zebraBackground: color.optional(),
      cellPaddingX: z.number().optional(),
      cellPaddingY: z.number().optional()
    })
    .partial(),
  card: z
    .object({
      background: color.optional(),
      borderColor: color.optional(),
      borderWidth: z.number().optional(),
      borderRadius: z.number().optional(),
      padding: z.number().optional(),
      shadow: z.boolean().optional()
    })
    .partial(),
  link: z.object({ color: color.optional(), underline: z.boolean().optional() }).partial(),
  spacing: z.object({ blockGap: z.number(), sectionPadding: z.number(), columnGap: z.number() })
});

const regionContent = z.object({ default: z.array(nodeSchema), firstPage: z.array(nodeSchema).optional() });

export const templateSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: z.string().min(1),
  name: z.string(),
  meta: z
    .object({
      createdAt: z.string().optional(),
      updatedAt: z.string().optional(),
      author: z.string().optional(),
      description: z.string().optional(),
      tags: z.array(z.string()).optional(),
      custom: z.record(z.unknown()).optional()
    })
    .partial()
    .optional(),
  page: pageSettingsSchema,
  styles: documentStylesSchema,
  header: regionContent,
  footer: regionContent,
  body: z.array(nodeSchema),
  library: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().optional(),
        category: z.string().optional(),
        node: nodeSchema,
        thumbnail: z.string().optional()
      })
    )
    .optional(),
  settings: z
    .object({
      locale: z.string().optional(),
      currency: z.string().optional(),
      timeZone: z.string().optional(),
      dataSources: z.array(z.string()).optional()
    })
    .partial()
    .optional()
});

export type TemplateSchemaInput = z.input<typeof templateSchema>;

export interface ParseResult {
  ok: boolean;
  template?: import("../types/template.js").ReportTemplate;
  errors: Array<{ path: string; message: string }>;
}

/** Validates the JSON shape of a template (after migration). */
export function parseTemplate(json: unknown): ParseResult {
  const r = templateSchema.safeParse(json);
  if (r.success) return { ok: true, template: r.data as import("../types/template.js").ReportTemplate, errors: [] };
  return { ok: false, errors: r.error.issues.map((i) => ({ path: i.path.join("."), message: i.message })) };
}
